import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import BackSection from '../../Services/BackSection';
import css from '../style.module.css';
import { NoDetailsDisplay } from "../NoTasksDisplay";
import AssignmentsConfirmation from './Assignment Details Parts/AssignmentsConfirmation';
import { AssignmentDetailsLoading } from '../../LoadingPages/LoadingAssignment';
import { fetchAssignmentDetails, updateAssignmentStatus } from '../AssignmentsApi';
import AssignmentDescription from './Assignment Details Parts/AssignmentDescription';
import AssignmentTaskFields from './Assignment Details Parts/AssignmentTaskFields';
import FailedToGetDetails from './Assignment Details Parts/FailedToGetDetails';

export default function AssignmentsDetails() {
    const { assignmentId } = useParams();
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const {
        data: assignment,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['assignmentDetails', assignmentId],
        queryFn: () => fetchAssignmentDetails(assignmentId),
        enabled: !!assignmentId,
    });

    // Update assignment status mutation
    const updateMutation = useMutation({
        mutationFn: (currentStatus) => updateAssignmentStatus(assignmentId, currentStatus),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['assignmentDetails', assignmentId]
            });
            await queryClient.invalidateQueries({
                queryKey: ['assignments']
            });
            setShowModal(false);
        },
        onError: (error) => {
            console.error("Error updating status:", error);
            alert("Failed to update assignment status. Please try again.");
        }
    });



    const handleUpdateClick = () => {
        setShowModal(true);
    };

    const handleConfirmUpdate = () => {
        if (!assignment) return;
        updateMutation.mutate(assignment.task_details.Status?.value);
    };

    // Priority color helper
    const getPriorityStyle = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return { bg: 'var(--accent-soft)', color: 'var(--accent)', icon: 'arrow-up' };
            case 'medium':
                return { bg: 'var(--warning-soft)', color: 'var(--warning)', icon: 'minus' };
            case 'low':
                return { bg: 'var(--info-soft)', color: 'var(--info)', icon: 'arrow-down' };
            default:
                return { bg: 'var(--surface-sunken)', color: 'var(--ink-soft)', icon: 'circle' };
        }
    };

    // Status color helper
    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return { bg: 'var(--success-soft)', color: 'var(--success)', icon: 'check-circle' };
            case 'not started':
                return { bg: 'var(--surface-sunken)', color: 'var(--ink-soft)', icon: 'circle' };
            default:
                return { bg: 'var(--surface-sunken)', color: 'var(--ink-soft)', icon: 'circle' };
        }
    };

    if (isLoading) {
        return (
            <>
                <BackSection pageName="Assignments" to={"/assignments"} />
                <AssignmentDetailsLoading />
            </>
        );
    }

    if (error) {
        return <FailedToGetDetails error={error} refetch={refetch} />
    };

    if (!assignment) {
        return (
            <>
                <BackSection pageName="Assignments" to={"/assignments"} />
                <NoDetailsDisplay />
            </>
        );
    }

    const priorityStyle = getPriorityStyle(assignment.task_details.Priority?.value);
    const statusStyle = getStatusStyle(assignment.task_details.Status?.value);


    return (
        <div>
            <BackSection pageName="Assignments" to={"/assignments"} />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                {/* Header Section */}
                <div className='d-flex align-items-start justify-content-between mb-4 flex-wrap gap-3'>
                    <div style={{ flex: 1 }}>
                        <h1
                            className='m-0 mb-2'
                            style={{
                                color: 'var(--ink)',
                                fontWeight: '700',
                                fontSize: '1.75rem',
                                lineHeight: '1.3'
                            }}
                        >
                            {assignment.task_details.Subject?.value}
                        </h1>
                        <div className="d-flex gap-2 align-items-center flex-wrap">
                            {/* Priority Badge */}
                            <span
                                className="badge d-inline-flex align-items-center"
                                style={{
                                    backgroundColor: priorityStyle.bg,
                                    color: priorityStyle.color,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600'
                                }}
                            >
                                <i className={`fas fa-${priorityStyle.icon} me-2`} style={{ fontSize: '0.7rem' }}></i>
                                {assignment.task_details.Priority?.value} Priority
                            </span>

                            {/* Status Badge */}
                            <span
                                className="badge d-inline-flex align-items-center"
                                style={{
                                    backgroundColor: statusStyle.bg,
                                    color: statusStyle.color,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600'
                                }}
                            >
                                <i className={`fas fa-${statusStyle.icon} me-2`} style={{ fontSize: '0.7rem' }}></i>
                                {assignment.task_details.Status?.value}
                            </span>
                        </div>
                    </div>

                    {/* Action Button */}
                    {assignment.task_details.showActionButton && (
                        <motion.button
                            className="btn d-flex align-items-center px-4 py-2"
                            style={{
                                backgroundColor: assignment.task_details.Status?.value === "Completed" ? 'white' : 'var(--accent)',
                                color: assignment.task_details.Status?.value === "Completed" ? 'var(--ink)' : 'white',
                                border: assignment.task_details.Status?.value === "Completed" ? '2px solid var(--hairline)' : 'none',
                                borderRadius: '10px',
                                fontWeight: '600',
                                fontSize: '0.95rem',
                                boxShadow: assignment.task_details.Status?.value === "Completed" ? 'none' : '0 4px 12px rgba(199, 29, 150, 0.2)'
                            }}
                            onClick={handleUpdateClick}
                            disabled={updateMutation.isPending}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {updateMutation.isPending ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                    Updating...
                                </>
                            ) : assignment.task_details.Status?.value === "Completed" ? (
                                <>
                                    <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                                    Reopen
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-check me-2"></i>
                                    Mark Complete
                                </>
                            )}
                        </motion.button>
                    )}
                </div>

                {/* Info Cards Grid */}
                <div className="row g-3 mb-4">
                    {/* Priority Card */}
                    <div className="col-md-4">
                        <motion.div
                            className="p-4 rounded-3 h-100"
                            style={{
                                border: '1px solid var(--hairline)',
                                backgroundColor: 'white'
                            }}
                            whileHover={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="d-flex align-items-center mb-2">
                                <div
                                    className="d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        backgroundColor: priorityStyle.bg
                                    }}
                                >
                                    <i className={`fas fa-${priorityStyle.icon}`} style={{ color: priorityStyle.color, fontSize: '1.2rem' }}></i>
                                </div>
                                <div>
                                    <p className="m-0 text-muted" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                                        {assignment.task_details.Priority?.label?.toUpperCase() || 'PRIORITY'}
                                    </p>
                                    <p className="m-0" style={{ color: 'var(--ink)', fontSize: '1.1rem', fontWeight: '700' }}>
                                        {assignment.task_details.Priority?.value}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Status Card */}
                    <div className="col-md-4">
                        <motion.div
                            className="p-4 rounded-3 h-100"
                            style={{
                                border: '1px solid var(--hairline)',
                                backgroundColor: 'white'
                            }}
                            whileHover={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="d-flex align-items-center mb-2">
                                <div
                                    className="d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        backgroundColor: statusStyle.bg
                                    }}
                                >
                                    <i className={`fas fa-${statusStyle.icon}`} style={{ color: statusStyle.color, fontSize: '1.2rem' }}></i>
                                </div>
                                <div>
                                    <p className="m-0 text-muted" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                                        {assignment.task_details.Status?.label?.toUpperCase() || 'STATUS'}
                                    </p>
                                    <p className="m-0" style={{ color: 'var(--ink)', fontSize: '1.1rem', fontWeight: '700' }}>
                                        {assignment.task_details.Status?.value}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Due Date Card */}
                    <div className="col-md-4">
                        <motion.div
                            className="p-4 rounded-3 h-100"
                            style={{
                                border: '1px solid var(--hairline)',
                                backgroundColor: 'white'
                            }}
                            whileHover={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="d-flex align-items-center mb-2">
                                <div
                                    className="d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(199, 29, 150, 0.1)'
                                    }}
                                >
                                    <i className="fas fa-calendar-alt" style={{ color: 'var(--accent)', fontSize: '1.2rem' }}></i>
                                </div>
                                <div>
                                    <p className="m-0 text-muted" style={{ fontSize: '0.8rem', fontWeight: '600' }}>
                                        {assignment.task_details.ActivityDate?.label?.toUpperCase() || 'DUE DATE'}
                                    </p>
                                    <p className="m-0" style={{ color: 'var(--ink)', fontSize: '1.1rem', fontWeight: '700' }}>
                                        {assignment.task_details.ActivityDate?.value || 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Description Section */}
                <AssignmentDescription assignmentId={assignmentId} description={assignment.task_details.Description?.value} />

                {/* TASK FIELDS SECTION (Expandable) */}
                {assignment?.label && assignment?.taskFields?.length > 0 && (
                    <AssignmentTaskFields label={assignment.label} taskFields={assignment.taskFields} />
                )}

                <AssignmentsConfirmation
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onConfirm={handleConfirmUpdate}
                    currentStatus={assignment.task_details.Status?.value}
                    assignmentSubject={assignment.task_details.Subject?.value}
                    isUpdating={updateMutation.isPending}
                />
            </motion.div>
        </div>
    );
}
